import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Home.css';

// Simple count-up hook
function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start || target === 0) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const steps = [
  {
    num: '01',
    title: 'Register as a Donor',
    desc: 'Contact our admin team to get registered in our database with your blood group and contact details.'
  },
  {
    num: '02',
    title: 'Get Verified',
    desc: 'Our team verifies your information and adds you to the active donor pool for your region.'
  },
  {
    num: '03',
    title: 'Receive a Call',
    desc: 'When someone needs your blood type, we connect you directly with hospitals and patients.'
  },
  {
    num: '04',
    title: 'Save a Life',
    desc: 'Donate at the nearest blood bank or hospital and become someone\'s hero today.'
  }
];

const Home = () => {
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  const donors = useCountUp(1200, 2000, statsVisible);
  const lives = useCountUp(3600, 2200, statsVisible);
  const cities = useCountUp(48, 1800, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Reveal on scroll
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
      },
      { threshold: 0.1 }
    );
    reveals.forEach(r => observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>BloodBridge — Every Drop Counts | Blood Donation India</title>
        <meta name="description" content="BloodBridge connects blood donors with those in urgent need. Find donors for A+, B+, O-, AB+ and all blood groups across India. Register today and save lives." />
        <meta name="keywords" content="blood donation, donate blood India, blood donors near me, blood bank, O negative blood, urgent blood need" />
        <link rel="canonical" href="https://blood.msdiscoveries.com" />
      </Helmet>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-bg-art">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`hero-circle hero-circle-${i + 1}`} />
          ))}
        </div>

        <div className="container hero-content">
          <div className="hero-badge badge animate-fade-in">
            <span className="badge-dot" />
            Over 1,200 registered donors
          </div>
          <h1 className="hero-title animate-fade-up">
            Every Drop<br />
            <span className="hero-title-accent">Saves a Life.</span>
          </h1>
          <p className="hero-desc animate-fade-up" style={{ animationDelay: '0.2s' }}>
            BloodBridge is India's most trusted blood donor network — 
            connecting generous donors with hospitals, families, and patients 
            who need urgent blood transfusions.
          </p>
          <div className="hero-actions animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <Link to="/contact" className="btn btn-primary">Become a Donor</Link>
            <Link to="/about" className="btn btn-outline">Learn More</Link>
          </div>

          <div className="hero-blood-groups animate-fade-up" style={{ animationDelay: '0.6s' }}>
            {bloodGroups.map((g, i) => (
              <div key={g} className="hbg-item" style={{ animationDelay: `${i * 0.05}s` }}>
                <span className="hbg-tag">{g}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-scroll-hint">
          <div className="scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div className="marquee-strip">
        <div className="marquee-track">
          {[...Array(3)].map((_, i) => (
            <span key={i} className="marquee-content">
              {bloodGroups.map(g => (
                <React.Fragment key={`${g}-${i}`}>
                  <span className="m-group">{g}</span>
                  <span className="m-sep">·</span>
                </React.Fragment>
              ))}
              <span className="m-text">DONATE BLOOD · SAVE LIVES ·</span>
              <span className="m-sep">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item reveal">
              <span className="stat-num">{donors.toLocaleString()}+</span>
              <span className="stat-label">Registered Donors</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item reveal reveal-delay-1">
              <span className="stat-num">{lives.toLocaleString()}+</span>
              <span className="stat-label">Lives Saved</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item reveal reveal-delay-2">
              <span className="stat-num">{cities}+</span>
              <span className="stat-label">Cities Covered</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item reveal reveal-delay-3">
              <span className="stat-num">24/7</span>
              <span className="stat-label">Emergency Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY DONATE ── */}
      <section className="why-section section section-dark">
        <div className="container">
          <div className="section-label reveal">Why Donate Blood</div>
          <div className="why-grid">
            <div className="why-text reveal">
              <h2 className="why-heading">
                The world runs on<br />
                <em>human generosity.</em>
              </h2>
              <p>
                Blood cannot be manufactured. It can only come from voluntary donors 
                like you. Every 2 seconds, someone in India needs blood — for surgeries, 
                accidents, cancer treatments, or childbirth emergencies.
              </p>
              <p>
                One donation — just 450ml — can save up to three lives. 
                It takes 10 minutes. You can donate every 3 months. 
                The impact lasts forever.
              </p>
              <Link to="/contact" className="btn btn-outline" style={{ marginTop: '32px' }}>
                Register Today
              </Link>
            </div>
            <div className="why-cards reveal reveal-delay-2">
              {[
                { icon: '🩸', title: '1 Donation = 3 Lives', desc: 'Your blood is separated into red cells, plasma, and platelets — each saving a different person.' },
                { icon: '⏱️', title: '10 Minutes', desc: 'The actual donation process takes less time than your lunch break.' },
                { icon: '🔄', title: 'Every 3 Months', desc: 'Your body replenishes blood naturally. Donate regularly, save regularly.' },
                { icon: '❤️', title: 'Health Benefits', desc: 'Regular donors have lower risk of heart disease and improved blood circulation.' }
              ].map((card, i) => (
                <div key={i} className="why-card">
                  <span className="why-card-icon">{card.icon}</span>
                  <h4>{card.title}</h4>
                  <p>{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-section section">
        <div className="container">
          <div className="section-label reveal">Process</div>
          <h2 className="how-title reveal">How It Works</h2>
          <div className="steps-grid">
            {steps.map((s, i) => (
              <div key={i} className={`step-card reveal reveal-delay-${i}`}>
                <span className="step-num">{s.num}</span>
                <div className="step-line" />
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOOD GROUPS ── */}
      <section className="groups-section section section-dark">
        <div className="container">
          <div className="section-label reveal">All Blood Types</div>
          <h2 className="groups-title reveal">We Need Every Blood Group</h2>
          <p className="groups-sub reveal">
            No matter your blood type, you can save lives. Each blood group is critical 
            for different patients and situations.
          </p>
          <div className="groups-grid">
            {bloodGroups.map((g, i) => (
              <div key={g} className={`group-card reveal reveal-delay-${i % 4}`}>
                <div className="group-circle">{g}</div>
                <div className="group-info">
                  <span className="group-name">Blood Group {g}</span>
                  <span className="group-stat">
                    {g === 'O+' && 'Most common — 37% of population'}
                    {g === 'O-' && 'Universal donor — rarest, most needed'}
                    {g === 'A+' && 'Second most common — 36%'}
                    {g === 'A-' && 'Rare and highly needed — 6%'}
                    {g === 'B+' && 'Common in South Asia — 9%'}
                    {g === 'B-' && 'Rare and critical — 2%'}
                    {g === 'AB+' && 'Universal recipient — 3%'}
                    {g === 'AB-' && 'Rarest of all — under 1%'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box reveal">
            <div className="cta-icon animate-heartbeat">
              <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M12 2C12 2 5 9.5 5 14a7 7 0 0 0 14 0C19 9.5 12 2 12 2z"/>
              </svg>
            </div>
            <h2>Ready to Save Lives?</h2>
            <p>
              Join thousands of heroes across India who donate blood regularly. 
              Reach out to us today and we'll have you registered in minutes.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">Contact Us Now</Link>
              <a href="tel:+919876543210" className="btn btn-outline">Call Emergency Line</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
